import { useCallback, useEffect, useMemo, useState } from 'react';
import { Badge, Button, Card, LoadingSkeleton, Select } from '@nexora/design-system';
import {
  useOnboardingPlan,
  useCreateOnboardingPlan,
  useTransitionOnboardingStep,
  useGoLive,
  STEP_HINT,
  STEP_LABEL,
  type OnboardingStep,
  type OnboardingStepState,
} from './useOnboarding';
import { useCompanies } from '../organization/useOrganization';
import { useAuth } from '../../shared/auth/AuthContext';
import { formatDate } from '../../shared/utils/format';

/**
 * Implantação — trilha de Go-Live de uma unidade.
 *
 * A tela anterior pedia que o usuário **digitasse o UUID da empresa** para
 * carregar o plano, e lia dez campos que a rota nunca devolveu (`checklist`,
 * `notes`, `assignedToId`, `id`, datas…): `GET /onboarding/:companyId`
 * respondia `{ progress, status, currentStep }`. A escada de etapas e os
 * rótulos eram constantes no componente, e o `status` de item de checklist
 * usava `COMPLETED`/`SKIPPED`, valores que `ChecklistItemStatus` não tem
 * (`DONE`/`NA`).
 *
 * Agora a empresa se escolhe numa lista e a escada vem do agregado, que
 * publica também quais transições ele aceita daqui — os botões de avançar
 * oferecem exatamente isso.
 */
export function OnboardingSetupWizardPage(): JSX.Element {
  const { tenant } = useAuth();
  const [companyId, setCompanyId] = useState('');
  const [toast, setToast] = useState<{ texto: string; variante: 'success' | 'error' } | null>(null);

  const empresasQ = useCompanies(tenant?.id ?? '');
  const { data: plan, isLoading, error, refetch } = useOnboardingPlan(companyId);
  const criar = useCreateOnboardingPlan();
  const transitar = useTransitionOnboardingStep();
  const goLive = useGoLive();

  const empresas = useMemo(() => empresasQ.data ?? [], [empresasQ.data]);

  /* Uma empresa selecionada de saída: pedir um UUID digitado era o que havia
     antes, e a lista de empresas sempre existiu em `/organization/companies`. */
  useEffect(() => {
    if (companyId) return;
    const primeira = empresas[0];
    if (primeira) setCompanyId(primeira.id);
  }, [empresas, companyId]);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  const avisar = useCallback((texto: string, variante: 'success' | 'error') => {
    setToast({ texto, variante });
  }, []);

  const abrirPlano = useCallback(async () => {
    if (!companyId) return;
    try {
      await criar.mutateAsync(companyId);
      avisar('Plano de implantação aberto.', 'success');
    } catch (err) {
      avisar(err instanceof Error ? err.message : 'Erro ao abrir o plano.', 'error');
    }
  }, [companyId, criar, avisar]);

  const avancar = useCallback(
    async (step: OnboardingStep) => {
      if (!companyId) return;
      try {
        const res = await transitar.mutateAsync({ companyId, step });
        avisar(`Etapa atual: ${STEP_LABEL[res.currentStep]}`, 'success');
      } catch (err) {
        avisar(err instanceof Error ? err.message : 'Erro na transição.', 'error');
      }
    },
    [companyId, transitar, avisar],
  );

  const liberar = useCallback(async () => {
    if (!companyId) return;
    try {
      await goLive.mutateAsync(companyId);
      avisar('Go-live aprovado.', 'success');
    } catch (err) {
      avisar(err instanceof Error ? err.message : 'Erro no go-live.', 'error');
    }
  }, [companyId, goLive, avisar]);

  const etapas = plan?.steps ?? [];
  const proximas = etapas.filter((e) => e.reachable);
  const etapaAtual = etapas.find((e) => e.current);
  const empresa = empresas.find((e) => e.id === companyId);

  /** Go-live é uma transição como as outras, mas tem rota própria. */
  const podeLiberar = proximas.some((e) => e.code === 'GO_LIVE');
  const emAndamento = criar.isPending || transitar.isPending || goLive.isPending;

  return (
    <div className="ob-page">
      <div className="ob-header">
        <div>
          <h1 className="font-display-lg text-display-lg text-on-surface m-0">Implantação</h1>
          <p className="font-body-md text-body-md text-on-surface-variant mt-xs">
            Trilha de go-live da unidade, na sequência que o sistema aceita.
          </p>
        </div>
        <div className="ob-picker">
          <Select
            label="Unidade"
            value={companyId}
            onChange={(e) => setCompanyId(e.target.value)}
            disabled={empresasQ.isLoading}
          >
            <option value="">Selecione…</option>
            {empresas.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </Select>
        </div>
      </div>

      {empresasQ.isLoading ? (
        <Card>
          <div className="ob-card">
            <LoadingSkeleton count={2} height="40px" />
          </div>
        </Card>
      ) : empresas.length === 0 ? (
        <Card>
          <div className="ob-card">
            <p className="ob-muted">
              Nenhuma empresa cadastrada. A implantação é por unidade — cadastre a
              empresa em <strong>Organização</strong> antes de abrir o plano.
            </p>
          </div>
        </Card>
      ) : null}

      {isLoading && companyId && (
        <Card>
          <div className="ob-card">
            <LoadingSkeleton count={4} height="40px" />
          </div>
        </Card>
      )}

      {error && (
        <Card>
          <div className="ob-card ob-state">
            <Badge variant="error" size="sm">
              erro
            </Badge>
            <p className="ob-hint">Não foi possível carregar o plano: {(error as Error).message}</p>
            <Button variant="secondary" size="sm" onClick={() => void refetch()}>
              Tentar de novo
            </Button>
          </div>
        </Card>
      )}

      {/* ── Sem plano ── */}
      {plan && !plan.exists && (
        <Card>
          <div className="ob-card">
            <h3 className="ob-section">Plano ainda não aberto</h3>
            <p className="ob-hint">
              {empresa ? <strong>{empresa.name}</strong> : 'Esta unidade'} não tem plano
              de implantação. Abrir o plano inicia a trilha na primeira etapa —
              nada é cobrado nem transmitido por isso.
            </p>
            <div className="ob-steps ob-steps--preview">
              {etapas.map((etapa) => (
                <div key={etapa.code} className="ob-step ob-step--futuro">
                  <span className="ob-step__index">{etapa.index + 1}</span>
                  <span className="ob-step__label">{STEP_LABEL[etapa.code]}</span>
                </div>
              ))}
            </div>
            <div>
              <Button variant="primary" loading={criar.isPending} onClick={() => void abrirPlano()}>
                Abrir plano de implantação
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* ── Com plano ── */}
      {plan?.exists && (
        <>
          <Card>
            <div className="ob-card">
              <div className="ob-progress">
                <div className="ob-progress__top">
                  <span className="ob-progress__pct">{plan.progress}%</span>
                  <Badge variant={plan.completedAt ? 'success' : 'info'} size="sm" dot>
                    {etapaAtual ? STEP_LABEL[etapaAtual.code] : STEP_LABEL[plan.currentStep]}
                  </Badge>
                </div>
                <div className="ob-bar">
                  <div className="ob-bar__fill" style={{ width: `${plan.progress}%` }} />
                </div>
                <p className="ob-hint">
                  {/* Percentual do servidor (`getProgress()`), não recalculado
                      aqui: é a posição na escada do próprio agregado. */}
                  Início{' '}
                  {plan.startedAt ? formatDate(plan.startedAt) : 'ainda não registrado'}
                  {plan.completedAt ? ` · go-live em ${formatDate(plan.completedAt)}` : ''}
                </p>
              </div>
            </div>
          </Card>

          {/* Próximo passo — a faixa da referência, com o texto da etapa atual */}
          <Card>
            <div className="ob-card ob-next">
              <div>
                <span className="ob-next__label">Próximo passo</span>
                <p className="ob-next__text">{STEP_HINT[plan.currentStep]}</p>
              </div>
              <div className="ob-next__actions">
                {proximas.length === 0 ? (
                  <span className="ob-muted">nenhuma transição disponível daqui</span>
                ) : (
                  proximas.map((etapa) => {
                    if (etapa.code === 'GO_LIVE') return null;
                    /* A escada aceita recuar: sem dizer isso, "Importacao de
                       cadastros" parece a proxima etapa quando e voltar uma. */
                    const voltar = etapaAtual ? etapa.index < etapaAtual.index : false;
                    return (
                      <Button
                        key={etapa.code}
                        variant={voltar ? 'ghost' : 'secondary'}
                        size="sm"
                        loading={transitar.isPending}
                        disabled={emAndamento}
                        onClick={() => void avancar(etapa.code)}
                      >
                        {voltar ? `Voltar para ${STEP_LABEL[etapa.code]}` : STEP_LABEL[etapa.code]}
                      </Button>
                    );
                  })
                )}
                {podeLiberar && (
                  <Button
                    variant="primary"
                    size="sm"
                    loading={goLive.isPending}
                    disabled={emAndamento}
                    onClick={() => void liberar()}
                  >
                    Aprovar go-live
                  </Button>
                )}
              </div>
            </div>
          </Card>

          {/* Escada */}
          <Card>
            <div className="ob-card">
              <h3 className="ob-section">Trilha</h3>
              <p className="ob-hint">
                A sequência é a do próprio sistema: só as etapas marcadas como
                disponíveis podem ser alcançadas daqui. Voltar uma etapa também é
                uma transição válida quando aparece na lista.
              </p>
              <div className="ob-steps">
                {etapas.map((etapa) => (
                  <PassoDaTrilha key={etapa.code} etapa={etapa} />
                ))}
              </div>
            </div>
          </Card>

          {/* O que a referência pede e não existe */}
          <Card>
            <div className="ob-card">
              <h3 className="ob-section">Fora desta tela</h3>
              <ul className="ob-list">
                <li>
                  <strong>Checklist item a item, com anexo de evidência</strong> — o
                  domínio tem <code>ChecklistEntity</code> com categoria,
                  responsável, evidência e transição por item, mas nada a persiste:
                  o agregado do plano não a carrega, não há porta de repositório e
                  nenhuma rota a expõe. Mostrar os itens aqui seria mostrar um
                  checklist que nunca salva.
                </li>
                <li>
                  <strong>Dias para o go-live planejado</strong> — não há data
                  planejada no plano; só a data em que o go-live foi aprovado.
                </li>
                <li>
                  <strong>Equipe de implantação, chat e reunião</strong> — o plano
                  tem <code>assignedToId</code> na tabela, que o agregado não lê, e
                  não há integração de chat nem de agenda.
                </li>
              </ul>
            </div>
          </Card>
        </>
      )}

      {toast && (
        <div role="alert" className={`ob-toast ob-toast--${toast.variante}`}>
          {toast.texto}
          <button onClick={() => setToast(null)} aria-label="Fechar" className="ob-toast__close">
            &times;
          </button>
        </div>
      )}

      <style>{`
        .ob-page { padding: 24px; display: flex; flex-direction: column; gap: 16px; }
        .ob-header { display: flex; justify-content: space-between; align-items: flex-end; gap: 16px; flex-wrap: wrap; margin-bottom: 8px; }
        .ob-picker { min-width: 260px; }
        .ob-card { padding: 16px; display: flex; flex-direction: column; gap: 12px; }
        .ob-section { font-family: Inter, sans-serif; font-size: 20px; line-height: 28px; font-weight: 600; color: #191c1e; margin: 0; }
        .ob-hint { font-size: 12px; line-height: 18px; color: #43474e; margin: 0; }
        .ob-muted { font-size: 12px; color: #74777f; margin: 0; }
        .ob-state { align-items: flex-start; }
        .ob-progress { display: flex; flex-direction: column; gap: 8px; }
        .ob-progress__top { display: flex; align-items: baseline; gap: 12px; }
        .ob-progress__pct { font-family: Inter, sans-serif; font-size: 24px; line-height: 32px; font-weight: 600; color: #191c1e; font-variant-numeric: tabular-nums; }
        .ob-bar { height: 8px; border-radius: 4px; background: #e0e3e5; overflow: hidden; }
        .ob-bar__fill { height: 100%; background: #002045; border-radius: 4px; }
        .ob-next { flex-direction: row; justify-content: space-between; align-items: center; gap: 16px; flex-wrap: wrap; background: #f2f4f6; }
        .ob-next__label { font-family: 'JetBrains Mono', ui-monospace, monospace; font-size: 12px; text-transform: uppercase; letter-spacing: 0.02em; color: #43474e; }
        .ob-next__text { margin: 4px 0 0; font-size: 12px; line-height: 18px; color: #191c1e; max-width: 62ch; }
        .ob-next__actions { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; }
        .ob-steps { display: flex; flex-direction: column; gap: 4px; }
        .ob-steps--preview { opacity: 0.85; }
        .ob-step { display: flex; align-items: center; gap: 12px; padding: 10px 12px; border-radius: 4px; border-left: 3px solid #c4c6cf; background: #f7f9fb; }
        .ob-step--concluido { border-left-color: #1a6b33; }
        .ob-step--atual { border-left-color: #002045; background: #f2f4f6; }
        .ob-step--disponivel { border-left-color: #86a0cd; }
        .ob-step--futuro { border-left-color: #e0e3e5; }
        .ob-step__index { font-family: 'JetBrains Mono', ui-monospace, monospace; font-size: 12px; font-weight: 500; width: 20px; height: 20px; display: inline-flex; align-items: center; justify-content: center; border-radius: 0.25rem; background: #e0e3e5; color: #43474e; flex: 0 0 auto; }
        .ob-step--atual .ob-step__index { background: #002045; color: #ffffff; }
        .ob-step--concluido .ob-step__index { background: #1a6b33; color: #ffffff; }
        .ob-step__label { font-size: 12px; color: #191c1e; flex: 1; }
        .ob-list { margin: 0; padding-left: 18px; font-size: 12px; line-height: 20px; color: #43474e; display: flex; flex-direction: column; gap: 6px; }
        .ob-toast { position: fixed; bottom: 1.5rem; right: 1.5rem; padding: 0.75rem 1rem; border-radius: 8px; font-size: 0.875rem; font-weight: 500; box-shadow: 0 4px 12px rgba(0,0,0,0.1); z-index: 10000; max-width: 400px; }
        .ob-toast--success { background: #d3f5d6; border: 1px solid #b6ebbb; color: #2e7d32; }
        .ob-toast--error { background: #ffdad6; border: 1px solid #ffb4ab; color: #ba1a1a; }
        .ob-toast__close { margin-left: 0.75rem; background: none; border: none; cursor: pointer; color: inherit; font-size: 1rem; padding: 0; line-height: 1; }
      `}</style>
    </div>
  );
}

function PassoDaTrilha({ etapa }: { etapa: OnboardingStepState }): JSX.Element {
  const classe = etapa.current
    ? 'ob-step ob-step--atual'
    : etapa.done
      ? 'ob-step ob-step--concluido'
      : etapa.reachable
        ? 'ob-step ob-step--disponivel'
        : 'ob-step ob-step--futuro';

  return (
    <div className={classe}>
      <span className="ob-step__index">{etapa.index + 1}</span>
      <span className="ob-step__label">{STEP_LABEL[etapa.code]}</span>
      {etapa.current ? (
        <Badge variant="info" size="sm" dot>
          etapa atual
        </Badge>
      ) : etapa.done ? (
        <Badge variant="success" size="sm" dot>
          concluída
        </Badge>
      ) : etapa.reachable ? (
        <Badge variant="neutral" size="sm">
          disponível
        </Badge>
      ) : null}
    </div>
  );
}
